import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, PHOTO_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  HandshakeIcon,
  PhoneIcon,
  EnvelopeSimpleIcon,
  TrashIcon,
  ArrowRightIcon,
  XIcon,
} from "@phosphor-icons/react";

const STATUSES = [
  { value: "new", label: "Νέα", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  { value: "contacted", label: "Επικοινωνία", color: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "interested", label: "Ενδιαφέρον", color: "bg-[#D1FAE5] text-[#065F46]" },
  { value: "not_interested", label: "Όχι", color: "bg-[#FEE2E2] text-[#991B1B]" },
  { value: "converted", label: "Καταχωρήθηκε", color: "bg-[#E0E7FF] text-[#3730A3]" },
  { value: "archived", label: "Αρχείο", color: "bg-[#F3F4F6] text-[#4B5563]" },
];

function statusObj(s) {
  return STATUSES.find((x) => x.value === s) || STATUSES[0];
}

export default function SellLeads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/sell-leads", { params: filter ? { status: filter } : {} });
      setLeads(r.data || []);
    } catch {
      toast.error("Αποτυχία φόρτωσης αιτήσεων");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/sell-leads/${id}/status`, { status });
      toast.success("Ενημερώθηκε");
      setLeads((cur) => cur.map((l) => (l.id === id ? { ...l, status } : l)));
      setSelected((cur) => (cur && cur.id === id ? { ...cur, status } : cur));
    } catch {
      toast.error("Αποτυχία");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή αίτησης; Η ενέργεια δεν αναιρείται.")) return;
    try {
      await api.delete(`/sell-leads/${id}`);
      toast.success("Διαγράφηκε");
      setLeads((cur) => cur.filter((l) => l.id !== id));
      setSelected(null);
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const convert = async (lead) => {
    try {
      const r = await api.post(`/sell-leads/${lead.id}/convert`);
      const prefill = r.data?.prefill;
      if (!prefill) throw new Error("no prefill");
      // Encode as base64 JSON — same shape AddCar already decodes from ?cargr=...
      const json = JSON.stringify(prefill);
      const b64 = btoa(unescape(encodeURIComponent(json)));
      toast.success("Μεταφορά στη φόρμα νέου αυτοκινήτου");
      nav(`/cars/new?cargr=${b64}`);
    } catch {
      toast.error("Αποτυχία μετατροπής");
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s.value] = leads.filter((l) => l.status === s.value).length;
    return acc;
  }, {});
  const totalNew = leads.filter((l) => l.status === "new").length;

  return (
    <div className="space-y-6" data-testid="sell-leads-page">
      <div className="flex items-center gap-3">
        <HandshakeIcon size={26} weight="regular" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Public Form</div>
          <h2 className="heading text-xl font-bold">Αιτήσεις Πώλησης Αυτοκινήτου</h2>
        </div>
        {totalNew > 0 && (
          <span className="ml-2 bg-[#E60000] text-white text-xs font-mono px-2 py-0.5">
            {totalNew} νέες
          </span>
        )}
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2" data-testid="status-filters">
        <button
          type="button"
          onClick={() => setFilter("")}
          className={`text-xs uppercase tracking-wider px-3 py-1.5 border ${filter === "" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15 hover:border-black/40"}`}
        >
          Όλες ({leads.length})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setFilter(s.value)}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 border ${filter === s.value ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15 hover:border-black/40"}`}
          >
            {s.label} {filter !== s.value ? `(${counts[s.value] || 0})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#4B5563] font-mono">Φόρτωση...</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-black/15 p-12 text-center text-[#4B5563]">
          <HandshakeIcon size={40} className="mx-auto opacity-50" />
          <div className="text-sm mt-3">Δεν υπάρχουν αιτήσεις για εμφάνιση</div>
        </div>
      ) : (
        <div className="border border-black/10 bg-white" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-black/10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-4 py-3">Αυτοκίνητο</th>
                <th className="px-4 py-3">Επικοινωνία</th>
                <th className="px-4 py-3">Φωτο</th>
                <th className="px-4 py-3">Κατάσταση</th>
                <th className="px-4 py-3">Ημερομηνία</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const st = statusObj(l.status);
                return (
                  <tr key={l.id} className="border-b border-black/5 hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.brand} {l.model} {l.variant}</div>
                      <div className="text-xs text-[#4B5563] font-mono mt-0.5">
                        {l.year} · {Number(l.mileage_km || 0).toLocaleString("el-GR")} km
                        {l.desired_price_eur > 0 ? ` · €${Number(l.desired_price_eur).toLocaleString("el-GR")}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-[#4B5563] mt-0.5 flex items-center gap-1">
                        <PhoneIcon size={12} /> {l.phone}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{(l.photos || []).length}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-mono ${st.color}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4B5563] font-mono">
                      {new Date(l.created_at).toLocaleString("el-GR")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ArrowRightIcon size={16} className="text-[#4B5563]" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <LeadDetail
          lead={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => updateStatus(selected.id, s)}
          onDelete={() => remove(selected.id)}
          onConvert={() => convert(selected)}
        />
      )}
    </div>
  );
}

function LeadDetail({ lead, onClose, onStatus, onDelete, onConvert }) {
  const st = statusObj(lead.status);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose} data-testid="lead-detail-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b border-black/10 px-6 py-4 flex items-start justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Αίτηση Πώλησης</div>
            <h2 className="heading text-xl font-bold mt-0.5">{lead.brand} {lead.model} {lead.variant} <span className="font-mono text-[#4B5563]">{lead.year}</span></h2>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-mono mt-2 inline-block ${st.color}`}>{st.label}</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5" aria-label="Κλείσιμο">
            <XIcon size={20} />
          </button>
        </header>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KV label="Χιλιόμετρα" value={`${Number(lead.mileage_km || 0).toLocaleString("el-GR")} km`} />
            <KV label="Καύσιμο" value={lead.fuel_type || "—"} />
            <KV label="Κιβώτιο" value={lead.transmission || "—"} />
            <KV label="VIN" value={lead.vin || "—"} mono />
            <KV label="Κατάσταση" value={lead.condition || "—"} />
            <KV label="Επιθυμητή τιμή" value={lead.desired_price_eur > 0 ? `€${Number(lead.desired_price_eur).toLocaleString("el-GR")}` : "—"} />
          </div>

          <div className="border-t border-black/10 pt-4">
            <h3 className="heading text-sm font-bold mb-3">Επικοινωνία</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><b>{lead.name}</b></div>
              <div><a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:underline"><PhoneIcon size={14} /> {lead.phone}</a></div>
              {lead.email ? <div><a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:underline"><EnvelopeSimpleIcon size={14} /> {lead.email}</a></div> : null}
              {lead.city ? <div className="text-[#4B5563]">📍 {lead.city}</div> : null}
            </div>
            {lead.comment ? (
              <div className="mt-4 bg-[#F9FAFB] border border-black/10 p-4 text-sm whitespace-pre-wrap">
                {lead.comment}
              </div>
            ) : null}
          </div>

          {lead.photos && lead.photos.length > 0 && (
            <div className="border-t border-black/10 pt-4">
              <h3 className="heading text-sm font-bold mb-3">Φωτογραφίες ({lead.photos.length})</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {lead.photos.map((p) => (
                  <a key={p.id} href={PHOTO_URL(p.url)} target="_blank" rel="noreferrer" className="aspect-square border border-black/10 overflow-hidden block">
                    <img src={PHOTO_URL(p.url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-black/10 pt-4">
            <h3 className="heading text-sm font-bold mb-3">Κατάσταση Διαχείρισης</h3>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => onStatus(s.value)}
                  className={`text-xs uppercase tracking-wider px-3 py-1.5 border ${lead.status === s.value ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15 hover:border-black/40"}`}
                  data-testid={`status-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <footer className="border-t border-black/10 px-6 py-4 flex items-center justify-between sticky bottom-0 bg-white gap-3">
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center gap-1.5 text-xs text-[#DC2626] hover:bg-[#FEE2E2] px-3 py-2 border border-transparent hover:border-[#FECACA]"
            data-testid="lead-delete-btn"
          >
            <TrashIcon size={14} /> Διαγραφή
          </button>
          <button
            type="button"
            onClick={onConvert}
            className="bg-[#0A0A0A] hover:bg-[#1F2937] text-white px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium flex items-center gap-2"
            data-testid="lead-convert-btn"
          >
            Μετατροπή σε Καταχώρηση <ArrowRightIcon size={14} />
          </button>
        </footer>
      </div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
